// src/components/forwardsCalculator/ForwardsTable.js
import { useState, useEffect, useRef } from 'react'
import { useForwardCurve } from '../hooks/ForwardCalculatorHooks'
import { RefSPOT } from '../hooks/MarketValuesHook'
import ForwardColumn from './ForwardColumn'
import { createColumn, deriveColumn, updateMaturity, updateExecutionDate, updateBankQuote, updateBankSpot } from './forwardsTableLogic'
import * as con from '../../GlobalConstants'
import { COUNTERPARTIES_NAMES, translateParameter } from '../../utils/translateFunctions'
import { getGeneratorDevaluationFunctionByDay, computeForwardValueFromDevaluationAndSpot } from '../../utils/forwardFunctions'

const MIN_COLUMNS = 2
const MAX_COLUMNS = 4

const ROWS = [
  'counterparty', 'type', 'executionDate', 'spot', 'nominal',
  'expiryDays', 'expirationDate',
  'refRate', 'refDevaluation',
  'bankSpot', 'bankRate', 'bankDevaluation',
  'fwdPoints', 'diffFwdPoints', 'diffCurvePxD',
  'diffSpotPxD', 'diffTotalPxD', 'diffTotal',
  'commands',
]

const HEADER_LABELS = {
  counterparty:    'Contraparte',
  type:            'Tipo',
  executionDate:   'Fecha de Ejecución',
  spot:            'SPOT',
  nominal:         'Nominal',
  expiryDays:      'Días Para Vencimiento',
  expirationDate:  'Fecha Vencimiento',
  refRate:         'Tasa FWD',
  refDevaluation:  'Devaluación',
  bankSpot:        'SPOT Banco',
  bankRate:        'Tasa FWD',
  bankDevaluation: 'Devaluación',
  fwdPoints:       'Puntos Forward de Ref.',
  diffFwdPoints:   'Dif. en Puntos FWD (Negativo = Costo)',
  diffCurvePxD:    'Dif. Curva Fwd Banco (PxD)',
  diffSpotPxD:     'Dif. Spot Banco (PxD)',
  diffTotalPxD:    'Dif. Pesos x Dólar total',
  diffTotal:       'Dif. Total Ref. - Banco',
  commands:        'Comandos',
}

// Filas que tienen título de sección con rowSpan
const SECTION_TITLES = {
  refRate:  { label: 'Devaluación Ref',   rowSpan: 2 },
  bankSpot: { label: 'Cotización Banco',  rowSpan: 3 },
}

// Filas que son la primera de su sección con título (no renderizan celda de título propia)
const SECTION_TITLE_CONTINUATION = new Set(['refDevaluation', 'bankRate', 'bankDevaluation'])

// Colores alternados por fila
const ROW_COLORS = {
  counterparty:    'even-row',
  type:            'uneven-row',
  executionDate:   'even-row',
  spot:            'uneven-row',
  nominal:         'even-row',
  expiryDays:      'uneven-row',
  expirationDate:  'even-row',
  refRate:         'even-row',
  refDevaluation:  'uneven-row',
  bankSpot:        'uneven-row',
  bankRate:        'even-row',
  bankDevaluation: 'uneven-row',
  fwdPoints:       'even-row',
  diffFwdPoints:   'uneven-row',
  diffCurvePxD:    'even-row',
  diffSpotPxD:     'uneven-row',
  diffTotalPxD:    'even-row',
  diffTotal:       'uneven-row',
  commands:        'even-row',
}

// ─── Tabla principal — posee el estado de todas las columnas ──────────────────

const ForwardsTable = () => {
  const refSpot      = RefSPOT()
  const forwardCurve = useForwardCurve()
  const [numColumns, setNumColumns] = useState(MIN_COLUMNS)

  // Estado de cada columna — array de MAX_COLUMNS columnas, siempre inicializadas
  const [cols, setCols] = useState(() =>
    Array.from({ length: MAX_COLUMNS }, () => createColumn(refSpot, forwardCurve))
  )

  // Ref para acceder a cols dentro de efectos sin re-suscribirse
  const colsRef = useRef(cols)
  colsRef.current = cols

  // Sincroniza spot y curva cuando llegan los valores reales
  useEffect(() => {
    if (!refSpot || !forwardCurve) return
    setCols(prev => prev.map(col => {
      const newSpot     = col.spotManual ? col.spot : refSpot
      const newBankSpot = col.bankSpot === 5000 ? refSpot : col.bankSpot
      const devalFn     = getGeneratorDevaluationFunctionByDay(
        forwardCurve[con.DAYS],
        forwardCurve[col.type === con.BUY ? con.BUY : con.SELL]
      )
      const bankDevaluation = devalFn(col.expiryDays)
      const bankRate = computeForwardValueFromDevaluationAndSpot(bankDevaluation, newBankSpot, col.expiryDays)
      return { ...col, spot: newSpot, bankSpot: newBankSpot, bankDevaluation, bankRate }
    }))
  }, [refSpot, forwardCurve])

  // Actualiza una columna por índice
  const updateCol = (index, updater) => {
    setCols(prev => prev.map((col, i) => i === index ? updater(col) : col))
  }

  // Construye los handlers para una columna específica
  const makeHandlers = (index) => ({
    setCounterparty: (name) => {
      const id = Object.keys(COUNTERPARTIES_NAMES).find(k => COUNTERPARTIES_NAMES[k] === name) || con.BLANK
      updateCol(index, col => ({ ...col, counterparty: id }))
    },
    setType: (val) => {
      const map = {
        [translateParameter(con.BUY)]: con.BUY,
        [translateParameter(con.SELL)]: con.SELL,
      }
      updateCol(index, col => ({ ...col, type: map[val] }))
    },
    setExecutionDate:   (val)        => updateCol(index, col => updateExecutionDate(col, val)),
    setSpot:            (val)        => updateCol(index, col => ({ ...col, spot: val })),
    setSpotManual:      (manual)     => updateCol(index, col => ({ ...col, spotManual: manual, spot: manual ? col.spot : refSpot })),
    setNominal:         (val)        => updateCol(index, col => ({ ...col, nominal: val })),
    setMaturity:        (field, val) => updateCol(index, col => updateMaturity(col, field, val)),
    setMaturityInput:   (field)      => updateCol(index, col => ({ ...col, maturityInput: field })),
    setBankSpot:        (val)        => updateCol(index, col => updateBankSpot(col, val)),
    setBankQuote:       (field, val) => updateCol(index, col => updateBankQuote(col, field, val)),
    setBankInputToggle: (field)      => updateCol(index, col => ({ ...col, bankInput: field })),
  })

  const addColumn    = () => { if (numColumns < MAX_COLUMNS) setNumColumns(prev => prev + 1) }
  const removeColumn = () => { if (numColumns > MIN_COLUMNS) setNumColumns(prev => prev - 1) }

  const colIndices = Array.from({ length: numColumns }, (_, i) => i)

  const SEPARATOR_ROWS = new Set(['refRate', 'bankSpot', 'fwdPoints'])

  return (
    <div className="forwards-table-wrapper" style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', width: '100%', maxWidth: '1400px' }}>

        <table className="forwards-table" style={{ width: '100%' }}>
          <tbody>

            {/* Header */}
            <tr>
              <td className="header-cell first-row" colSpan={2} style={{ fontWeight: 'bold' }}>COTIZACIÓN</td>
              {colIndices.map(i => (
                <td key={i} className="header-cell first-row" style={{ fontWeight: 'bold' }}>FORWARD {i + 1}</td>
              ))}
            </tr>

            {/* Filas */}
            {ROWS.map(row => (
              <>
                {/* Separador entre secciones */}
                {SEPARATOR_ROWS.has(row) && (
                  <tr key={`sep-${row}`} style={{ height: '16px', backgroundColor: 'transparent' }}>
                    <td colSpan={numColumns + 2} style={{ border: 'none', padding: 0 }} />
                  </tr>
                )}

                <tr key={row} className={ROW_COLORS[row]}>

                  {/* Título de sección con rowSpan */}
                  {SECTION_TITLES[row] && (
                    <td
                      className={`header-cell dark-blue`}
                      rowSpan={SECTION_TITLES[row].rowSpan}
                      style={{ width: '105px', whiteSpace: 'nowrap', verticalAlign: 'middle' }}
                    >
                      {SECTION_TITLES[row].label}
                    </td>
                  )}

                  {/* Celda de atributo */}
                  {!SECTION_TITLE_CONTINUATION.has(row) && !SECTION_TITLES[row] ? (
                    <td className={`header-cell dark-blue ${ROW_COLORS[row]}`} colSpan={2} style={{ whiteSpace: 'nowrap' }}>
                      {HEADER_LABELS[row]}
                    </td>
                  ) : (
                    <td className={`header-cell dark-blue ${ROW_COLORS[row]}`} style={{ width: '105px', whiteSpace: 'nowrap' }}>
                      {HEADER_LABELS[row]}
                    </td>
                  )}

                  {/* Celdas de datos */}
                  {colIndices.map(i => (
                    <ForwardColumn
                      key={i}
                      col={cols[i]}
                      derived={deriveColumn(cols[i], forwardCurve)}
                      refSpot={refSpot}
                      handlers={makeHandlers(i)}
                      row={row}
                      evenRow={ROW_COLORS[row] === 'even-row'}
                    />
                  ))}
                </tr>
              </>
            ))}

          </tbody>
        </table>

        {/* Botones dinámicos al lado derecho */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0, paddingTop: '4px' }}>
          <button onClick={addColumn} disabled={numColumns >= MAX_COLUMNS}
            className="boton-mas-menos"
            style={{ backgroundColor: 'rgb(242, 184, 0)', color: 'black', borderRadius: '8px', padding: '4px 12px', fontWeight: 'bold', fontSize: '18px', cursor: numColumns >= MAX_COLUMNS ? 'not-allowed' : 'pointer', opacity: numColumns >= MAX_COLUMNS ? 0.5 : 1 }}
          >+</button>
          <button onClick={removeColumn} disabled={numColumns <= MIN_COLUMNS}
            className="boton-mas-menos"
            style={{ backgroundColor: 'rgb(242, 184, 0)', color: 'black', borderRadius: '8px', padding: '4px 12px', fontWeight: 'bold', fontSize: '18px', cursor: numColumns <= MIN_COLUMNS ? 'not-allowed' : 'pointer', opacity: numColumns <= MIN_COLUMNS ? 0.5 : 1 }}
          >−</button>
        </div>

      </div>
    </div>
  )
}

export default ForwardsTable